import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import {
  getGoogleLanguage,
  getGoogleLanguageCodes,
  setGoogleLanguage,
} from 'utils/googleTranslate';
import { googleLanguageNames } from 'utils/googleLanguageNames';

export const LANGUAGE_KEY = 'jcgn_delivery_language';

export const getSavedLanguage = () => localStorage.getItem(LANGUAGE_KEY) || 'en';

const toOption = (code) => ({ code, label: googleLanguageNames[code] || code });

const byLabel = (a, b) => a.label.localeCompare(b.label);

const INITIAL_OPTIONS = Object.keys(googleLanguageNames).map(toOption).sort(byLabel);

function LanguagePicker({ width, placement }) {
  const [options, setOptions] = useState(INITIAL_OPTIONS);
  const [currentCode, setCurrentCode] = useState(
    () => localStorage.getItem(LANGUAGE_KEY) || getGoogleLanguage()
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    getGoogleLanguageCodes().then((codes) => {
      if (active && codes.length) setOptions(['en', ...codes].map(toOption).sort(byLabel));
    });
    return () => {
      active = false;
    };
  }, []);

  const handleChange = async (_event, lang) => {
    if (!lang) return;
    setLoading(true);
    localStorage.setItem(LANGUAGE_KEY, lang.code);
    setCurrentCode(lang.code);
    try {
      await setGoogleLanguage(lang.code);
    } finally {
      setLoading(false);
    }
  };

  const value = options.find((o) => o.code === currentCode) || toOption(currentCode);

  return (
    <Autocomplete
      className="notranslate"
      translate="no"
      options={options}
      value={value}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.code === selected.code}
      disableClearable
      blurOnSelect
      size="small"
      disabled={loading}
      componentsProps={{
        popper: { className: 'notranslate', placement, sx: { zIndex: 2000 } },
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.code}>
          {option.label}
        </li>
      )}
      sx={{
        width,
        '& .MuiOutlinedInput-root': {
          borderRadius: '40px',
          backgroundColor: '#fff',
          paddingLeft: '14px !important',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Language"
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Language',
            onBlur: (event) => {
              params.inputProps.onBlur?.(event);
              // Long names otherwise stay scrolled to the caret and show only their ending.
              event.target.scrollLeft = 0;
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

LanguagePicker.defaultProps = {
  width: 220,
  placement: 'top',
};

LanguagePicker.propTypes = {
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  placement: PropTypes.string,
};

export default LanguagePicker;
